import type {CSS} from '@pinecast/styles';
import styled from '@pinecast/styles';
import * as React from 'react';

const DURATION = 1.5;

const animation = {
  from: {
    backgroundPosition: '125% top',
  },
  to: {
    backgroundPosition: '-100% top',
  },
} as any;

function useDelay() {
  const ref = React.useRef<HTMLSpanElement | HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    if (ref.current) {
      const left = ref.current.getBoundingClientRect().left;
      const delayRatio = left / window.innerWidth;
      ref.current.style.animationDelay = `${delayRatio * DURATION}s`;
    }
  }, []);
  return ref;
}

type CommonProps = {
  neutralColor?: string;
  sheenColor?: string;
  style?: CSS;
};

type InlineProps = CommonProps & {width?: string};

const InlineSkeletonBase = styled(
  'span',
  (
    {
      $neutralColor,
      $sheenColor,
      $width = '60%',
    }: {
      $neutralColor?: string;
      $sheenColor?: string;
      $width?: string;
    },
    designColors,
  ) => ({
    animationDuration: `${DURATION}s`,
    animationName: animation,
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    backgroundClip: 'padding-box',
    backgroundColor: designColors.borderGrayLight,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '400% 100%',
    borderRadius: '4px',
    cursor: 'default',
    pointerEvents: 'none',
    userSelect: 'none',
    willChange: 'background-position',

    '::before': {visibility: 'hidden'},
    '::after': {visibility: 'hidden'},
    backgroundImage: `linear-gradient(135deg, ${
      $neutralColor ?? designColors.borderGrayLight
    } 25%, ${$sheenColor ?? designColors.borderGrayNormal} 50%, ${
      $neutralColor ?? designColors.borderGrayLight
    } 75%)`,
    display: 'inline-block',
    height: '1em',
    maxWidth: '160px',
    verticalAlign: 'middle',
    width: $width,
  }),
);

export const InlineSkeleton = (props: InlineProps) => {
  const ref = useDelay();
  return (
    <InlineSkeletonBase
      $neutralColor={props.neutralColor}
      $sheenColor={props.sheenColor}
      $width={props.width}
      ref={ref}
      style={props.style}
    />
  );
};

const BlockSkeletonBase = styled(
  'div',
  (
    {
      $neutralColor,
      $sheenColor,
    }: {
      $neutralColor?: string;
      $sheenColor?: string;
    },
    designColors,
  ) => ({
    animationDuration: `${DURATION}s`,
    animationName: animation,
    animationIterationCount: 'infinite',
    animationTimingFunction: 'linear',
    backgroundClip: 'padding-box',
    backgroundColor: designColors.borderGrayLight,
    backgroundRepeat: 'no-repeat',
    backgroundSize: '400% 100%',
    borderRadius: '4px',
    cursor: 'default',
    pointerEvents: 'none',
    userSelect: 'none',
    willChange: 'background-position',

    '::before': {visibility: 'hidden'},
    '::after': {visibility: 'hidden'},
    backgroundImage: `linear-gradient(135deg, ${
      $neutralColor ?? designColors.borderGrayLight
    } 25%, ${$sheenColor ?? designColors.borderGrayNormal} 50%, ${
      $neutralColor ?? designColors.borderGrayLight
    } 75%)`,
    display: 'block',
  }),
);

export const BlockSkeleton = (props: CommonProps) => {
  const ref = useDelay();
  return (
    <BlockSkeletonBase
      $neutralColor={props.neutralColor}
      $sheenColor={props.sheenColor}
      ref={ref as React.Ref<HTMLDivElement>}
      style={props.style}
    />
  );
};
